<template>
  <TransitionRoot appear as="template" :show="isVisible">
    <Dialog as="div" class="relative z-[10000]" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4" @click.stop>
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-gray-800">
                <div class="flex items-center justify-between p-6 border-b border-gray-600">
                  <h3 class="text-lg font-semibold leading-6 text-white">
                    {{ isEditMode ? 'Edit Category' : 'Add Category' }}
                  </h3>
                  <button type="button" @click="closeDialog" :disabled="isLoading"
                    class="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6">
                  <div class="space-y-6">
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Name *</label>
                      <input v-model="formData.name" type="text" maxlength="100"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.name }" />
                      <p v-if="errors.name" class="mt-1 text-sm text-red-400">{{ errors.name }}</p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Description</label>
                      <textarea v-model="formData.description" rows="4" maxlength="500"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.description }" placeholder="Enter category description" />
                      <p v-if="errors.description" class="mt-1 text-sm text-red-400">{{ errors.description }}</p>
                    </div>

                    <!-- check-box -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Status</label>
                      <input type="checkbox" v-model="formData.status" class="">
                      <p v-if="errors.status" class="mt-1 text-sm text-red-400">{{ errors.status }}</p>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                  <button @click="closeDialog" type="button" :disabled="isLoading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 border border-gray-500 rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                  <button @click="handleSubmit" type="button" :disabled="isLoading || imageUploading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <template v-if="isLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </template>
                    <template v-else>{{ isEditMode ? 'Update' : 'Create' }}</template>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp, collection } from 'firebase/firestore';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  categoryId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['onEmitHandleEvent']);

const isLoading = ref(false);
const imageUploading = ref(false);
const formData = ref({
  name: '',
  description: '',
  status: true,
});
const errors = ref({});

const isEditMode = computed(() => !!props.categoryId);

const closeDialog = () => {
  formData.value = {
    name: '',
    description: '',
    status: true,
  };
  emit('onEmitHandleEvent', 'close');
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.name.trim()) {
    errors.value.name = 'Name is required.';
    isValid = false;
  }

  if (formData.value.description && formData.value.description.length > 500) {
    errors.value.description = 'Description cannot exceed 500 characters.';
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (isLoading.value || imageUploading.value) return;

  if (!validateForm()) return;

  isLoading.value = true;
  try {
    const categoryData = {
      name: formData.value.name.trim(),
      description: formData.value.description.trim() || '',
      status: formData.value.status,
      updatedAt: serverTimestamp(),
    };

    if (!isEditMode.value) {
      categoryData.createdAt = serverTimestamp();
    }

    const categoryRef = doc(projectFirestore, 'categories', props.categoryId || doc(collection(projectFirestore, 'categories')).id);
    await setDoc(categoryRef, categoryData, { merge: isEditMode.value });

    emit('onEmitHandleEvent', isEditMode.value ? 'updated' : 'created');
  } catch (error) {
    console.error('Error saving category:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to save category. Please try again.');
  } finally {
    isLoading.value = false;
    imageUploading.value = false;
  }
};

const fetchCategory = async () => {
  if (!props.categoryId) return;
  try {
    const categoryRef = doc(projectFirestore, 'categories', props.categoryId);
    const categoryDoc = await getDoc(categoryRef);
    if (categoryDoc.exists()) {
      formData.value = { ...formData.value, ...categoryDoc.data() };
    } else {
      emit('onEmitHandleEvent', 'error', true, 'Category not found.');
    }
  } catch (error) {
    console.error('Error fetching category:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to load category data.');
  }
};

watch(
  () => props.categoryId,
  (newCategoryId) => {
    if (newCategoryId) {
      fetchCategory();
    } else {
      // Reset formData and errors when switching to add mode
      formData.value = {
        name: '',
        description: '',
        status: true,
      };
      errors.value = {};
    }
  },
  { immediate: true }
);
</script>

<style scoped>
input,
select,
textarea {
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #3b82f6;
  /* blue-500 */
}

button:disabled {
  cursor: not-allowed;
}
</style>